import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Loader } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import ViMap from "@/components/vi/ViMap";
import { readCache, writeCache } from "@/lib/offline";
import { Navigation, RefreshCw, MapPinOff } from "lucide-react";

const here = () => new Promise((res) => {
  if (!navigator.geolocation) return res(null);
  navigator.geolocation.getCurrentPosition((p) => res(p.coords), () => res(null), { timeout: 6000, maximumAge: 120000 });
});

export default function RouteCard({ tourId }) {
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    const pos = await here();
    const qs = `tournee_id=${tourId}${pos ? `&lat=${pos.latitude}&lng=${pos.longitude}` : ""}`;
    try { const res = await api.get(`/itinerant/route?${qs}`); writeCache("route", res.data); setR(res.data); }
    catch { setR(readCache("route") || { stops: [], sans_gps: [] }); }
    setBusy(false);
  };
  useEffect(() => { load(); }, [tourId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!r) return <Loader />;
  const markers = [
    ...(r.depart ? [{ id: "dep", pos: [r.depart.lat, r.depart.lng], label: "D", color: "#334155", popup: r.depart.source }] : []),
    ...r.stops.map((s) => ({ id: s.id, pos: [s.lat, s.lng], label: String(s.rang), popup: <b>{s.rang}. {s.nom}</b> })),
  ];
  const line = [...(r.depart ? [[r.depart.lat, r.depart.lng]] : []), ...r.stops.map((s) => [s.lat, s.lng])];
  return (
    <div data-testid="route-card">
      <div className="flex items-center justify-between gap-2 mb-2">
        <p className="text-sm">{r.stops.length} arrêt(s) · <b className="font-mono" data-testid="route-distance">{r.distance_km} km</b> <span className="text-[11px] text-slate-400">estimés</span></p>
        <span className="flex gap-1">
          <Button size="sm" variant="outline" onClick={load} disabled={busy} data-testid="route-refresh"><RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /></Button>
          {r.nav_url && <a href={r.nav_url} target="_blank" rel="noreferrer"><Button size="sm" className="bg-brand-600 hover:bg-brand-700 text-white" data-testid="route-nav"><Navigation className="h-3.5 w-3.5 mr-1" />Itinéraire</Button></a>}
        </span>
      </div>
      {r.depart && <p className="text-[11px] text-slate-500 mb-2" data-testid="route-start">Départ : {r.depart.source}</p>}
      {r.stops.length > 0 && <ViMap markers={markers} lines={[{ id: "route", points: line, dashed: true }]} height={260} testid="route-map" />}
      <ol className="mt-2 space-y-1">
        {r.stops.map((s) => (
          <li key={s.id} className="flex items-start gap-2 text-sm rounded-lg border bg-white px-2 py-1.5" data-testid={`route-stop-${s.rang}`}>
            <span className="h-6 w-6 shrink-0 rounded-full bg-brand-600 text-white text-xs font-bold flex items-center justify-center">{s.rang}</span>
            <span className="flex-1"><Link to={`/vi/scan/c/${s.id}`} className="font-semibold hover:underline">{s.nom}</Link><br /><span className="text-[11px] text-slate-500">{s.raisons.join(" · ")}</span></span>
            <span className="text-[11px] font-mono text-slate-500 whitespace-nowrap">+{s.distance_km} km</span>
          </li>
        ))}
      </ol>
      {r.sans_gps?.length > 0 && <p className="text-[11px] text-slate-500 mt-2" data-testid="route-no-gps"><MapPinOff className="inline h-3.5 w-3.5 mr-1" />Sans position GPS ({r.sans_gps.length}) : {r.sans_gps.map((x) => x.nom).join(", ")} — position enregistrée au premier check-in sur place.</p>}
      {!r.stops.length && !r.sans_gps?.length && <p className="text-xs text-slate-400">Aucune boutique recommandée aujourd'hui.</p>}
    </div>
  );
}
